// components/userManagement/UserBulkCreateTab.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { bulkCreateStudentUsers, clearBulkCreateResult } from '../../slices/usersSlice';
import { useToast } from '../../context/ToastContext';
import axios from 'axios';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

const UserBulkCreateTab = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { loading, bulkCreateResult } = useSelector((state) => state.users);
  const [formData, setFormData] = useState({
    class_id: '',
    student_ids: [],
  });
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const headers = { 'Authorization': `Bearer ${token}` };
        
        const [classRes, stuRes] = await Promise.all([
          axios.get(`${BASE_URL}/students/classes/`, { headers }),
          axios.get(`${BASE_URL}/students/`, { headers }),
        ]);
        
        setClasses(classRes.data || []);
        setStudents(stuRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearBulkCreateResult());
    };
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'class_id' && { student_ids: [] }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const bulkData = formData.class_id
        ? { class_id: formData.class_id }
        : { student_ids: formData.student_ids };
      
      await dispatch(bulkCreateStudentUsers(bulkData)).unwrap();
      showToast('تم إنشاء حسابات الطلاب بنجاح', 'success');
      setFormData({ class_id: '', student_ids: [] });
      onSuccess();
    } catch (error) {
      const errorMsg = error.detail || error.message || 'حدث خطأ أثناء الإنشاء المجمع';
      showToast(errorMsg, 'error');
    }
  };

  const filteredStudents = formData.class_id
    ? students.filter(s => s.school_class?.id === formData.class_id)
    : students;

  return (
    <Box sx={{ p: 3 }}>
      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            select
            label="الصف (اختياري - سيتم إنشاء حسابات لجميع طلاب الصف)"
            name="class_id"
            value={formData.class_id}
            onChange={handleChange}
            fullWidth
          >
            <MenuItem value="">اختر الصف</MenuItem>
            {classes.map((cls) => (
              <MenuItem key={cls.id} value={cls.id}>
                {cls.name}
              </MenuItem>
            ))}
          </TextField>

          {!formData.class_id && (
            <Alert severity="info">
              يمكنك تحديد طلاب محددين أو اختيار صف كامل
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            disabled={loading || (!formData.class_id && formData.student_ids.length === 0)}
            fullWidth
            size="large"
          >
            {loading ? <CircularProgress size={24} /> : 'إنشاء حسابات الطلاب'}
          </Button>
        </Stack>
      </form>

      {bulkCreateResult && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            نتائج الإنشاء المجمع
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            تم إنشاء: {bulkCreateResult.summary?.created || 0} | 
            تم التخطي: {bulkCreateResult.summary?.skipped || 0} | 
            فشل: {bulkCreateResult.summary?.failed || 0}
          </Alert>

          {bulkCreateResult.details?.created?.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>اسم الطالب</TableCell>
                    <TableCell>اسم المستخدم</TableCell>
                    <TableCell>كلمة المرور</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkCreateResult.details.created.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.student_name}</TableCell>
                      <TableCell>{item.username}</TableCell>
                      <TableCell>{item.password}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default UserBulkCreateTab;
